import { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import MaskAndValid from "utils/maskAndValid";
import groupPrePedidoItens from "../utils/groupPrePedidoItens";

const THEME = { primary: "#003366" };

function formatDate(value) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
}

function formatVigencia(inicio, fim) {
  if (!inicio && !fim) return "—";
  if (inicio && fim) return `${inicio} - ${fim}`;
  return inicio || fim;
}

export default function PrePedidoDetailDrawer({
  open,
  onClose,
  detalhe,
  loading = false,
}) {
  const [expandedSections, setExpandedSections] = useState(new Set());

  const grouped = useMemo(
    () => groupPrePedidoItens(detalhe?.Itens || detalhe?.itens),
    [detalhe],
  );

  const valorTotal = Number(detalhe?.ValorTotal ?? detalhe?.valorTotal) || 0;
  const valorMin = Number(
    detalhe?.ValorMinimoVitrine ?? detalhe?.valorMinimoVitrine,
  );
  const qtdMin = Number(
    detalhe?.QuantidadeMinimaPedido ?? detalhe?.quantidadeMinimaPedido,
  );
  const qtdTotal = (detalhe?.Itens || detalhe?.itens || []).reduce(
    (acc, i) => acc + Number(i.Quantidade ?? i.quantidade ?? 0),
    0,
  );
  const valOk = !valorMin || valorMin <= 0 || valorTotal >= valorMin;
  const qtyOk = !qtdMin || qtdMin <= 0 || qtdTotal >= qtdMin;

  const toggleSection = (id) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const nomeVitrine =
    detalhe?.NomeVitrine || detalhe?.nomeVitrine || "Vitrine B2B";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      className="pre-pedido-detail-drawer cart-modal-drawer"
      PaperProps={{ className: "pre-pedido-detail-paper cart-modal-paper" }}
    >
      <Box className="cart-modal-container pre-pedido-detail-container">
        <Box className="cart-modal-header">
          <Box className="cart-header-left">
            <ReceiptLongIcon
              className="cart-header-icon"
              style={{ color: THEME.primary }}
            />
            <Typography
              className="cart-header-title brand-font"
              style={{ color: THEME.primary }}
            >
              Detalhe do pré-pedido
            </Typography>
          </Box>
          <Box className="cart-header-right">
            <Button onClick={onClose} className="close-btn">
              <CloseIcon />
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box className="pre-pedido-detail-loading">
            <CircularProgress size={36} />
          </Box>
        ) : !detalhe ? (
          <Box className="pre-pedido-detail-loading">
            <Typography color="textSecondary">
              Selecione um pré-pedido para ver os detalhes.
            </Typography>
          </Box>
        ) : (
          <Box className="cart-modal-content custom-scroll pre-pedido-detail-body">
            <Box className="pre-pedido-detail-meta">
              <Typography variant="body2" style={{ color: THEME.secondary }}>
                <strong>CNPJ:</strong>{" "}
                {MaskAndValid.maskCNPJ(
                  detalhe.CnpjLoja || detalhe.cnpjLoja || "",
                ) || "—"}
              </Typography>
              <Typography variant="body2" style={{ color: THEME.secondary }}>
                <strong>Consumidor:</strong>{" "}
                {detalhe.RazaoSocialLoja || detalhe.razaoSocialLoja || "—"}
              </Typography>
              <Typography variant="body2" style={{ color: THEME.secondary }}>
                <strong>Data:</strong>{" "}
                {formatDate(detalhe.DataPrePedido || detalhe.dataPrePedido)}
              </Typography>
              <Typography variant="body2" style={{ color: THEME.secondary }}>
                <strong>Vigência:</strong>{" "}
                {formatVigencia(
                  detalhe.VigenciaInicioFormat || detalhe.vigenciaInicioFormat,
                  detalhe.VigenciaFimFormat || detalhe.vigenciaFimFormat,
                )}
              </Typography>
              <Typography variant="body2" style={{ color: THEME.secondary }}>
                <strong>Status:</strong>{" "}
                {detalhe.Status || detalhe.status || "Criado"}
              </Typography>
            </Box>

            <Box className="cart-vitrine-section">
              <Box className="cart-vitrine-header">
                <Typography className="vitrine-label">
                  VITRINE: {nomeVitrine}
                </Typography>
                <Box className="vitrine-header-right">
                  {valorMin > 0 && (
                    <Box
                      className={`validation-badge ${
                        valOk ? "val-success" : "val-error"
                      }`}
                    >
                      Mín. R$ {MaskAndValid.maskPrice(valorMin)}
                    </Box>
                  )}
                  {qtdMin > 0 && (
                    <Box
                      className={`validation-badge ${
                        qtyOk ? "val-success" : "val-error"
                      }`}
                      style={{ marginLeft: valorMin > 0 ? 8 : 0 }}
                    >
                      Mín. {qtdMin} un
                    </Box>
                  )}
                  <Typography className="vitrine-total">
                    R$ {MaskAndValid.maskPrice(valorTotal)}
                  </Typography>
                </Box>
              </Box>

              <Box className="cart-vitrine-content">
                {Object.keys(grouped)
                  .sort()
                  .map((catName) => {
                    const subcats = grouped[catName];
                    let catQty = 0;
                    let catVal = 0;
                    Object.values(subcats).forEach((lines) => {
                      lines.forEach((line) => {
                        catQty +=
                          Number(line.Quantidade ?? line.quantidade) || 0;
                        catVal +=
                          Number(
                            line.ValorTotalLinha ?? line.valorTotalLinha,
                          ) || 0;
                      });
                    });

                    return (
                      <Box key={catName} className="cart-category-section">
                        <Box className="cart-category-header">
                          <Typography
                            className="category-name"
                            style={{ color: THEME.primary }}
                          >
                            {catName}
                          </Typography>
                          <Typography className="pre-pedido-cat-summary">
                            R$ {MaskAndValid.maskPrice(catVal)} em {catQty}{" "}
                            itens
                          </Typography>
                        </Box>

                        {Object.keys(subcats)
                          .sort()
                          .map((subName) => {
                            const lines = subcats[subName];
                            let subQty = 0;
                            let subVal = 0;
                            lines.forEach((line) => {
                              subQty +=
                                Number(line.Quantidade ?? line.quantidade) || 0;
                              subVal +=
                                Number(
                                  line.ValorTotalLinha ?? line.valorTotalLinha,
                                ) || 0;
                            });
                            const accordId = `${catName}-${subName}`.replace(
                              /\s+/g,
                              "-",
                            );
                            const isExpanded = expandedSections.has(accordId);

                            return (
                              <Box
                                key={subName}
                                className="cart-subcategory-block"
                              >
                                <Box
                                  className="cart-accordion-header"
                                  onClick={() => toggleSection(accordId)}
                                  role="button"
                                  tabIndex={0}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      toggleSection(accordId);
                                    }
                                  }}
                                >
                                  <Typography
                                    style={{
                                      fontSize: 14,
                                      fontWeight: 700,
                                      color: "#374151",
                                    }}
                                  >
                                    {subName}
                                  </Typography>
                                  <Typography
                                    component="span"
                                    className="pre-pedido-sub-summary"
                                  >
                                    R$ {MaskAndValid.maskPrice(subVal)} em{" "}
                                    {subQty} itens
                                  </Typography>
                                  {isExpanded ? (
                                    <ExpandLessIcon
                                      style={{
                                        width: 20,
                                        height: 20,
                                        color: "#6b7280",
                                      }}
                                    />
                                  ) : (
                                    <ExpandMoreIcon
                                      style={{
                                        width: 20,
                                        height: 20,
                                        color: "#6b7280",
                                      }}
                                    />
                                  )}
                                </Box>

                                {isExpanded && (
                                  <Box className="cart-accordion-content pre-pedido-lines">
                                    {lines.map((line, idx) => {
                                      const nome =
                                        line.NomeProduto ||
                                        line.nomeProduto ||
                                        line.IdProduto ||
                                        line.idProduto;
                                      const qtd =
                                        Number(
                                          line.Quantidade ?? line.quantidade,
                                        ) || 0;
                                      const unit =
                                        Number(
                                          line.ValorUnitario ??
                                            line.valorUnitario,
                                        ) || 0;
                                      const bruto =
                                        Number(
                                          line.ValorUnitarioBruto ??
                                            line.valorUnitarioBruto,
                                        ) || 0;
                                      const total =
                                        Number(
                                          line.ValorTotalLinha ??
                                            line.valorTotalLinha,
                                        ) || unit * qtd;
                                      const cond =
                                        line.CondicaoPagamento ||
                                        line.condicaoPagamento;

                                      return (
                                        <Box
                                          key={`${line.IdProduto || line.idProduto}-${idx}`}
                                          className="pre-pedido-line-row"
                                        >
                                          <Box className="pre-pedido-line-main">
                                            <Typography className="pre-pedido-line-name">
                                              {nome}
                                            </Typography>
                                            {cond ? (
                                              <Typography className="pre-pedido-line-cond">
                                                Condição: {cond}
                                              </Typography>
                                            ) : null}
                                          </Box>
                                          <Box className="pre-pedido-line-prices">
                                            {bruto > unit && (
                                              <Typography className="pre-pedido-line-gross">
                                                R${" "}
                                                {MaskAndValid.maskPrice(bruto)}
                                              </Typography>
                                            )}
                                            <Typography className="pre-pedido-line-unit">
                                              R$ {MaskAndValid.maskPrice(unit)}
                                            </Typography>
                                            <Typography className="pre-pedido-line-qty">
                                              Qtd: {qtd}
                                            </Typography>
                                            <Typography
                                              className="pre-pedido-line-total"
                                              style={{ color: THEME.primary }}
                                            >
                                              R$ {MaskAndValid.maskPrice(total)}
                                            </Typography>
                                          </Box>
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                )}
                              </Box>
                            );
                          })}
                      </Box>
                    );
                  })}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
